/**
 * PricingFormModal — Модалка создания/редактирования прайс-листа
 */
import React, { useState, useEffect } from 'react';
import {
  Modal, Stack, TextInput, NumberInput, Select, Textarea, Button, Group,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';

const ORG_TYPE_OPTIONS = [
  { value: 'b2c', label: 'B2C — Оплата родителями' },
  { value: 'b2b', label: 'B2B — Компания' },
  { value: 'individual', label: 'Индивидуальные' },
  { value: 'competition', label: 'Соревнования' },
];

const PricingFormModal = ({ opened, onClose, onSuccess, plan = null }) => {
  const isEdit = !!plan;

  const [formData, setFormData] = useState({
    organization_type: '',
    name: '',
    single_lesson_default: null,
    subscription_size: 4,
    subscription_default: null,
    family_discount_percent: 0,
    family_discount_min_children: 2,
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (plan) {
      setFormData({
        organization_type: plan.organization_type || '',
        name: plan.name || '',
        single_lesson_default: plan.single_lesson_default ? Number(plan.single_lesson_default) : null,
        subscription_size: plan.subscription_size || 4,
        subscription_default: plan.subscription_default ? Number(plan.subscription_default) : null,
        family_discount_percent: plan.family_discount_percent ? Number(plan.family_discount_percent) : 0,
        family_discount_min_children: plan.family_discount_min_children || 2,
        notes: plan.notes || '',
      });
    } else {
      setFormData({
        organization_type: '',
        name: '',
        single_lesson_default: null,
        subscription_size: 4,
        subscription_default: null,
        family_discount_percent: 0,
        family_discount_min_children: 2,
        notes: '',
      });
    }
  }, [plan, opened]);

  const handleSubmit = async () => {
    if (!formData.organization_type) {
      notifications.show({ title: 'Ошибка', message: 'Выберите тип организации', color: 'red' });
      return;
    }
    if (!formData.name.trim()) {
      notifications.show({ title: 'Ошибка', message: 'Введите название', color: 'red' });
      return;
    }

    setLoading(true);
    try {
      const url = isEdit ? `/api/pricing/${plan.id}` : '/api/pricing';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Ошибка сохранения');
      }

      notifications.show({
        title: 'Успешно',
        message: isEdit ? 'Прайс-лист обновлён' : 'Прайс-лист создан',
        color: 'green',
      });
      onSuccess();
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Редактирование прайс-листа' : 'Новый прайс-лист'}
      centered
      radius="lg"
      size="md"
    >
      <Stack gap="md">
        <Select
          label="Тип организации"
          placeholder="Выберите тип"
          data={ORG_TYPE_OPTIONS}
          value={formData.organization_type}
          onChange={(val) => setFormData((prev) => ({ ...prev, organization_type: val || '' }))}
          disabled={isEdit}
          required
          radius="xl"
        />

        <TextInput
          label="Название"
          placeholder={formData.organization_type === 'competition' ? 'Например: Соревнования' : 'Например: B2C — Оплата родителями'}
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          required
        />

        {formData.organization_type === 'competition' ? (
          /* Соревнования: только цена участия */
          <NumberInput
            label="Цена участия за ребёнка (₽)"
            placeholder="2000"
            value={formData.single_lesson_default}
            onChange={(val) => setFormData((prev) => ({ ...prev, single_lesson_default: val }))}
            min={0}
            decimalScale={2}
            radius="xl"
          />
        ) : (
          /* Занятия: разовое + абонемент + размер */
          <>
            <Group grow>
              <NumberInput
                label="Разовое занятие (₽)"
                placeholder="1900"
                value={formData.single_lesson_default}
                onChange={(val) => setFormData((prev) => ({ ...prev, single_lesson_default: val }))}
                min={0}
                decimalScale={2}
                radius="xl"
              />
              <NumberInput
                label="Абонемент (₽)"
                placeholder="5850"
                value={formData.subscription_default}
                onChange={(val) => setFormData((prev) => ({ ...prev, subscription_default: val }))}
                min={0}
                decimalScale={2}
                radius="xl"
              />
            </Group>

            <NumberInput
              label="Размер абонемента (занятий)"
              value={formData.subscription_size}
              onChange={(val) => setFormData((prev) => ({ ...prev, subscription_size: val }))}
              min={1}
              radius="xl"
            />
          </>
        )}

        <Group grow>
          <NumberInput
            label="Семейная скидка (%)"
            value={formData.family_discount_percent}
            onChange={(val) => setFormData((prev) => ({ ...prev, family_discount_percent: val }))}
            min={0}
            max={100}
            decimalScale={2}
            radius="xl"
          />
          <NumberInput
            label="Мин. детей для скидки"
            value={formData.family_discount_min_children}
            onChange={(val) => setFormData((prev) => ({ ...prev, family_discount_min_children: val }))}
            min={1}
            radius="xl"
          />
        </Group>

        <Textarea
          label="Заметки"
          placeholder="Дополнительная информация"
          value={formData.notes}
          onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
          radius="xl"
          autosize
          minRows={2}
        />

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Отмена</Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default PricingFormModal;
